"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getAddressSafes = exports.isAddressAMultisigMember = exports.getGnosisSafeInfo = exports.isAGnosisSafeTx = exports.getSafeHomeLink = exports.getSafeDashboardLink = exports.getSafeWalletConnectLink = exports.getBaseSafeAppUrl = exports.getGnosisSafeTxServiceBaseUrl = exports.getGnosisChainPrefixByChainId = exports.getGnosisChainNameByChainId = exports.clearSafeCache = exports.getSafeApiKey = exports.setSafeApiKey = void 0;
const chains_1 = require("@wagmi/chains");
const axios_1 = __importDefault(require("axios"));
const ethers_1 = require("ethers");
const config_1 = require("../config");
// Safe API Key configuration - must be set by consuming application
let safeApiKey;
const setSafeApiKey = (apiKey) => {
    safeApiKey = apiKey;
};
exports.setSafeApiKey = setSafeApiKey;
const getSafeApiKey = () => safeApiKey;
exports.getSafeApiKey = getSafeApiKey;
const getSafeAxiosConfig = () => {
    if (!safeApiKey)
        return {};
    return {
        headers: {
            Authorization: `Bearer ${safeApiKey}`,
        },
    };
};
// ============ Rate Limiting & Caching ============
// In-memory cache with TTL (5 minutes)
const CACHE_TTL_MS = 5 * 60 * 1000;
const memoryCache = new Map();
// In-flight request deduplication
const pendingRequests = new Map();
// Rate limiting: max 4 requests per second (staying under Safe's 5/sec limit)
const REQUEST_INTERVAL_MS = 250;
let lastRequestTime = 0;
const requestQueue = [];
let isProcessingQueue = false;
const processQueue = () => {
    if (isProcessingQueue || requestQueue.length === 0)
        return;
    isProcessingQueue = true;
    const processNext = () => {
        if (requestQueue.length === 0) {
            isProcessingQueue = false;
            return;
        }
        const now = Date.now();
        const timeSinceLastRequest = now - lastRequestTime;
        const delay = Math.max(0, REQUEST_INTERVAL_MS - timeSinceLastRequest);
        setTimeout(() => {
            const next = requestQueue.shift();
            if (next) {
                lastRequestTime = Date.now();
                next();
            }
            processNext();
        }, delay);
    };
    processNext();
};
const rateLimitedRequest = async (url) => {
    // Check memory cache first
    const cached = memoryCache.get(url);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
        return cached.data;
    }
    // Check for in-flight request
    const pending = pendingRequests.get(url);
    if (pending) {
        return pending;
    }
    // Create new request with rate limiting
    const requestPromise = new Promise((resolve, reject) => {
        requestQueue.push(async () => {
            try {
                const response = await axios_1.default.get(url, getSafeAxiosConfig());
                memoryCache.set(url, { data: response.data, timestamp: Date.now() });
                resolve(response.data);
            }
            catch (error) {
                reject(error);
            }
            finally {
                pendingRequests.delete(url);
            }
        });
        processQueue();
    });
    pendingRequests.set(url, requestPromise);
    return requestPromise;
};
// Clear cache for a specific key (useful when data changes)
const clearSafeCache = (key) => {
    if (key) {
        memoryCache.delete(key);
    }
    else {
        memoryCache.clear();
    }
};
exports.clearSafeCache = clearSafeCache;
const getGnosisChainNameByChainId = (chainId) => {
    switch (chainId) {
        case chains_1.mainnet.id:
            return "mainnet";
        case chains_1.goerli.id:
            return "goerli";
        case chains_1.arbitrum.id:
            return "arbitrum";
        case chains_1.optimism.id:
            return "optimism";
        case chains_1.polygon.id:
            return "polygon";
        case chains_1.avalanche.id:
            return "avalanche";
        case chains_1.bsc.id:
            return "bsc";
        case chains_1.gnosis.id:
            return "gnosis-chain";
        case chains_1.sepolia.id:
            return "sepolia";
        case config_1.oasis.id:
            return "oasis";
        default:
            throw new Error(`Gnosis doesn't support chainId:${chainId} yet`);
    }
};
exports.getGnosisChainNameByChainId = getGnosisChainNameByChainId;
const getGnosisChainPrefixByChainId = (chainId) => {
    switch (chainId) {
        case chains_1.mainnet.id:
            return "eth";
        case chains_1.goerli.id:
            return "gor";
        case chains_1.arbitrum.id:
            return "arb1";
        case chains_1.optimism.id:
            return "oeth";
        case chains_1.polygon.id:
            return "matic";
        case chains_1.avalanche.id:
            return "avax";
        case chains_1.bsc.id:
            return "bnb";
        case chains_1.bsc.id:
            return "bnb";
        case chains_1.gnosis.id:
            return "gno";
        case chains_1.sepolia.id:
            return "sep";
        case config_1.oasis.id:
            return "sapphire";
        default:
            throw new Error(`Gnosis doesn't support chainId:${chainId} yet`);
    }
};
exports.getGnosisChainPrefixByChainId = getGnosisChainPrefixByChainId;
const getGnosisSafeTxServiceBaseUrl = (chainId) => {
    if (chainId === config_1.oasis.id)
        return `https://transaction.safe.oasis.io`;
    // New Safe API URL format using EIP3770 chain prefixes
    return `https://api.safe.global/tx-service/${(0, exports.getGnosisChainPrefixByChainId)(chainId)}`;
};
exports.getGnosisSafeTxServiceBaseUrl = getGnosisSafeTxServiceBaseUrl;
const getBaseSafeAppUrl = (chainId) => {
    if (chainId === config_1.oasis.id)
        return `https://safe.oasis.io`;
    return `https://app.safe.global`;
};
exports.getBaseSafeAppUrl = getBaseSafeAppUrl;
const getSafeWalletConnectLink = (address, chainId) => {
    const safeDashboardLink = (0, exports.getSafeDashboardLink)(address, chainId);
    if (!safeDashboardLink)
        return undefined;
    return `${safeDashboardLink}&appUrl=https%3A%2F%2Fapps-portal.safe.global%2Fwallet-connect`;
};
exports.getSafeWalletConnectLink = getSafeWalletConnectLink;
const getSafeDashboardLink = (address, chainId) => {
    try {
        if (!chainId)
            return "";
        const checksummedSafeAddress = ethers_1.utils.getAddress(address);
        if (chainId === config_1.oasis.id)
            return `https://safe.oasis.io/apps/open?safe=${(0, exports.getGnosisChainPrefixByChainId)(chainId)}:${checksummedSafeAddress}`;
        return `https://app.safe.global/apps/open?safe=${(0, exports.getGnosisChainPrefixByChainId)(chainId)}:${checksummedSafeAddress}`;
    }
    catch (error) {
        return undefined;
    }
};
exports.getSafeDashboardLink = getSafeDashboardLink;
const getSafeHomeLink = (address, chainId) => {
    try {
        if (!chainId)
            return "";
        const checksummedSafeAddress = ethers_1.utils.getAddress(address);
        if (chainId === config_1.oasis.id)
            return `https://safe.oasis.io/home?safe=${(0, exports.getGnosisChainPrefixByChainId)(chainId)}:${checksummedSafeAddress}`;
        return `https://app.safe.global/home?safe=${(0, exports.getGnosisChainPrefixByChainId)(chainId)}:${checksummedSafeAddress}`;
    }
    catch (error) {
        return undefined;
    }
};
exports.getSafeHomeLink = getSafeHomeLink;
const getGnosisTxsApiEndpoint = (txHash, chainId) => {
    try {
        if (!chainId)
            return "";
        return `${(0, exports.getGnosisSafeTxServiceBaseUrl)(chainId)}/api/v2/multisig-transactions/${txHash}`;
    }
    catch (error) {
        return undefined;
    }
};
const getGnosisSafeStatusApiEndpoint = (safeAddress, chainId) => {
    try {
        if (!chainId)
            return "";
        const checksummedSafeAddress = ethers_1.utils.getAddress(safeAddress);
        // Note: /safes/{address}/ endpoint is only available in v1
        return `${(0, exports.getGnosisSafeTxServiceBaseUrl)(chainId)}/api/v1/safes/${checksummedSafeAddress}`;
    }
    catch (error) {
        return undefined;
    }
};
const getAddressSafesApiEndpoint = (walletAddress, chainId) => {
    try {
        if (!chainId)
            return "";
        // Note: /owners/{address}/safes/ endpoint is only available in v1
        return `${(0, exports.getGnosisSafeTxServiceBaseUrl)(chainId)}/api/v1/owners/${walletAddress}/safes`;
    }
    catch (error) {
        return undefined;
    }
};
const isAGnosisSafeTx = async (tx, chainId) => {
    try {
        if (!chainId)
            throw new Error("Please provide chainId");
        const safeUrl = getGnosisTxsApiEndpoint(tx, chainId);
        if (!safeUrl)
            throw new Error("No url");
        const data = await rateLimitedRequest(safeUrl);
        return !!(data === null || data === void 0 ? void 0 : data.safeTxHash);
    }
    catch (error) {
        return false;
    }
};
exports.isAGnosisSafeTx = isAGnosisSafeTx;
const getGnosisSafeInfo = async (address, chainId) => {
    var _a;
    try {
        if (!chainId || !address)
            throw new Error("Please provide address and chainId");
        const safeUrl = getGnosisSafeStatusApiEndpoint(address, chainId);
        if (!safeUrl)
            throw new Error("No url");
        // Use rate-limited request with in-memory caching
        const data = await rateLimitedRequest(safeUrl);
        if (!data)
            throw new Error("No data");
        return {
            isSafeAddress: (_a = data.isSafeAddress) !== null && _a !== void 0 ? _a : true,
            owners: data.owners,
            threshold: data.threshold,
        };
    }
    catch (error) {
        return {
            isSafeAddress: false,
            owners: [],
            threshold: 0,
        };
    }
};
exports.getGnosisSafeInfo = getGnosisSafeInfo;
const isAddressAMultisigMember = async (multisigAddress, address, chainId) => {
    if (!multisigAddress || !chainId || !address)
        return false;
    const members = (await (0, exports.getGnosisSafeInfo)(multisigAddress, Number(chainId))).owners;
    const isMember = members.map((add) => add.toLowerCase()).includes(address.toLowerCase());
    return isMember;
};
exports.isAddressAMultisigMember = isAddressAMultisigMember;
const getAddressSafes = async (walletAddress, chainId) => {
    var _a;
    try {
        if (!chainId)
            throw new Error("Please provide chainId");
        const safeUrl = getAddressSafesApiEndpoint(walletAddress, chainId);
        if (!safeUrl)
            throw new Error("No url");
        // Use rate-limited request with in-memory caching
        const data = await rateLimitedRequest(safeUrl);
        if (!data)
            throw new Error("No data");
        return (_a = data.safes) !== null && _a !== void 0 ? _a : [];
    }
    catch (error) {
        return [];
    }
};
exports.getAddressSafes = getAddressSafes;
